import { useState, useEffect, useRef } from "react";
import { initMasonry } from "../../../common/initMasonry.js";
import { string, bool, objectOf, any, shape, number } from "prop-types";
import useSearchValues from "../../../common/useSearchValues.js";
import useFetchJson from "../../../common/useFetchJson.js";
import {
  scrollToResults,
  getInjectable,
  metaParser,
} from "../../../common/helpers.js";
import useEvent from "../../../common/useEvent.js";
import SearchError from "../components/views/SearchError.jsx";
import KeywordSearch from "../components/KeywordSearch/KeywordSearch.jsx";
import FilterBar from "../components/FilterBar/FilterBar.jsx";
import ResultsInfo from "../components/ResultsInfo/ResultsInfo.jsx";
import SortSelected from "../components/views/SortSelected.jsx";
import LoadingSpinner from "../components/LoadingSpinner/LoadingSpinner.jsx";
import Pagination from "../components/Pagination/Pagination.jsx";

const defaultProps = {
  id: "search",
};

const propTypes = {
  model: shape({
    id: string.isRequired,
    url: string.isRequired,
    hideMobileSummary: bool,
    featuredUrl: string,
    dictionary: objectOf(any).isRequired,
    loadMore: bool,
    uniqueId: string,
    perPage: number,
  }),
};

const SearchApp = (props) => {
  const { model, queryStringState, setQueryStringState } = props;
  const { url, id, perPage, dictionary, uniqueId } = model;

  const customEv = useEvent("newResults", () =>
    scrollToResults(`#${id}-result-container`)
  );
  const { apiQueryString, searchValues } = useSearchValues();
  const page = useRef(1);

  const [injectableComponents, setInjectableComponents] = useState();

  const serverData = useFetchJson(`${url}${apiQueryString}`, {
    credentials: "same-origin",
    method: "GET",
  });

  const [initial, setInitial] = useState(true);

  const hasResults = serverData.data?.search_results?.length > 0;

  const parsedFacets = metaParser(serverData.data);

  const masonryOptions = {
    containerSelector: ".search-container--staggered .search__results",
    itemSelector: ".search__result-item",
    columnWidth: ".search__result-item",
    gutter: 30, // must match --masonry-gutter in CSS
    horizontalOrder: true,
  };

  useEffect(() => {
    if (initial) {
      setInjectableComponents(getInjectable(`#${uniqueId}`));
    }

    /*
    if the page arg has changed and this is not the
    initial load dispatch the new page load event.
    */
    if (!initial) {
      page.current = searchValues.page;
      customEv.dispatchEv();
    }
    setInitial(false);
  }, [searchValues.page]);

  useEffect(() => {
    initMasonry(masonryOptions);
  }, [serverData]);

  return serverData.error ? (
    <SearchError text={dictionary.searchError} />
  ) : (
    <div className="search__app" id={id}>
      <div className="search__container">
        <div className="search__results-wrapper">
          {serverData?.isLoading && <LoadingSpinner />}
          <div
            id={`${id}-result-container`}
            tabIndex="-1"
            role="region"
            aria-live="polite"
            aria-atomic="false"
          >
            <div className="search__header">
              {(serverData?.data?.filters.includes("keyword") ||
                queryStringState?.keyword?.length > 0) && (
                <KeywordSearch
                  typeaheadApi={serverData?.data?.system?.typeahead}
                  classNames="search__input"
                  keywords={String(queryStringState?.keyword || "")}
                  handleEntry={(value) =>
                    setQueryStringState({ keyword: value, page: 0 })
                  }
                  dictionary={dictionary}
                  inputOptions={{
                    type: "text",
                    id: "search",
                    name: "search",
                    minLength: dictionary.searchMinLength,
                  }}
                />
              )}
              {serverData?.data?.pager?.total_results > 0 && (
                <div className="search__results-info">
                  <ResultsInfo
                    totalResults={serverData?.data?.pager?.total_results}
                    isLoading={serverData?.isLoading}
                    perPage={
                      serverData?.data?.pager?.items_per_page || perPage || 10
                    }
                  />
                </div>
              )}
              {parsedFacets?.length > 0 && (
                <div className="search__filter-bar">
                  <FilterBar facets={parsedFacets} />
                </div>
              )}
              {injectableComponents && (
                <div
                  className="injected-content"
                  dangerouslySetInnerHTML={{ __html: injectableComponents }}
                />
              )}
              <SortSelected serverData={serverData} />
            </div>
          </div>

          {hasResults && (
            <div className="search__results section" id={`${id}-results`}>
              {serverData?.data?.search_results.map((result, i) => {
                return (
                  <div
                    className="search__result-item"
                    key={i}
                    dangerouslySetInnerHTML={{ __html: result.search_item }}
                  ></div>
                );
              })}
            </div>
          )}

          {hasResults && serverData?.data?.pager?.total_results && (
            <Pagination
              totalResults={Number(serverData?.data?.pager?.total_results)}
              perPage={serverData?.data?.pager?.items_per_page || perPage || 10}
            />
          )}

          {!serverData.isLoading && !hasResults && (
            <div className="search__no-results">
              {dictionary.noResultsMessage || "No Results Found"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

SearchApp.defaultProps = defaultProps;
SearchApp.propTypes = propTypes;
export default SearchApp;
